import { useState, useEffect, useRef, useCallback } from 'react';

const MAX_NICK_ATTEMPTS = 5;

/**
 * Manages MUC (Multi-User Chat) room interaction via a Strophe connection.
 * Handles joining, messages, occupant count, and moderation actions.
 *
 * Duplicate nickname handling: if Prosody rejects the join with a 409 conflict
 * error, the hook retries up to MAX_NICK_ATTEMPTS times using a numeric suffix
 * ("Maija 2", "Maija 3", ...).  effectiveNickname reflects the nick that actually
 * joined.  joinError is set to true if all attempts fail.
 */
export function useChatRoom(connection, roomJid, nickname, status) {
  const [messages, setMessages] = useState([]);
  const [occupantCount, setOccupantCount] = useState(0);
  const [occupantRoles, setOccupantRoles] = useState({});
  const [ownRole, setOwnRole] = useState('participant');
  const [kicked, setKicked] = useState(false);
  const [joinError, setJoinError] = useState(false);
  const [effectiveNickname, setEffectiveNickname] = useState(null);
  const handlersRef = useRef([]);
  const occupantsRef = useRef(new Map());
  const joinedRef = useRef(false);
  const ownRoleRef = useRef('participant');
  // Mutable ref so the presence handler can retry without triggering re-renders.
  const nickAttemptRef = useRef(0);
  const effectiveNickRef = useRef(nickname);

  useEffect(() => {
    if (status !== 'CONNECTED' || !connection || !roomJid || !nickname) {
      return;
    }

    if (typeof window === 'undefined') return;

    let cancelled = false;

    // Reset nick tracking whenever the base nickname or connection changes.
    nickAttemptRef.current = 0;
    effectiveNickRef.current = nickname;

    async function sendJoinPresence($pres, nickToTry, requestHistory = true) {
      const joinPres = $pres({ to: `${roomJid}/${nickToTry}` })
        .c('x', { xmlns: 'http://jabber.org/protocol/muc' })
        .c('history', { maxstanzas: requestHistory ? '50' : '0' });
      connection.send(joinPres);
    }

    async function joinRoom() {
      const { $pres } = await import('strophe.js');

      if (cancelled) return;

      // Handler for incoming groupchat messages
      const msgHandler = connection.addHandler(
        (stanza) => {
          if (cancelled) return true;

          const from = stanza.getAttribute('from') || '';
          const type = stanza.getAttribute('type');

          if (type !== 'groupchat') return true;
          if (from !== roomJid && !from.startsWith(`${roomJid}/`)) return true;

          const body = stanza.getElementsByTagName('body')[0];

          // Check for message retraction/moderation (XEP-0425)
          const applyTo = stanza.getElementsByTagName('apply-to')[0];
          const moderated = stanza.getElementsByTagName('moderated')[0];
          if (applyTo || moderated) {
            const retractedId =
              applyTo?.getAttribute('id') ||
              moderated?.getAttribute('id') ||
              null;
            if (retractedId) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === retractedId || m.archiveId === retractedId
                    ? { ...m, retracted: true }
                    : m,
                ),
              );
            }
            return true;
          }

          if (!body) return true;

          // Extract stanza-id stamped by the MUC room (used for retractions).
          // Messages can have multiple <stanza-id> elements; we need the one
          // where by="room@conference..." (the room bare JID).
          const roomBareJid = roomJid.split('/')[0];
          let archiveId = null;
          const stanzaIdEls = stanza.getElementsByTagName('stanza-id');
          for (let i = 0; i < stanzaIdEls.length; i++) {
            if (stanzaIdEls[i].getAttribute('by') === roomBareJid) {
              archiveId = stanzaIdEls[i].getAttribute('id');
              break;
            }
          }
          // Fallback: if only one stanza-id exists, use it regardless of 'by'
          if (!archiveId && stanzaIdEls.length === 1) {
            archiveId = stanzaIdEls[0].getAttribute('id');
          }
          const id =
            archiveId || stanza.getAttribute('id') || Date.now().toString();

          const text = body.textContent;
          const nick = from.split('/')[1] || '';
          const delay = stanza.getElementsByTagName('delay')[0];
          const timestamp = delay
            ? new Date(delay.getAttribute('stamp')).getTime()
            : Date.now();

          setMessages((prev) => [
            ...prev,
            {
              id,
              archiveId: archiveId || null,
              nick,
              text,
              timestamp,
              retracted: false,
              isSelf: nick === effectiveNickRef.current,
              role: occupantsRef.current.get(nick) || 'participant',
            },
          ]);

          return true;
        },
        null,
        'message',
        null,
        null,
        null,
      );

      // Handler for private messages from bot (moderation confirmations)
      const pmHandler = connection.addHandler(
        (stanza) => {
          if (cancelled) return true;

          const from = stanza.getAttribute('from') || '';
          const type = stanza.getAttribute('type');
          if (type !== 'chat') return true;
          // Only accept PMs from our room (bot sends from room/BotNick)
          if (!from.startsWith(`${roomJid}/`)) return true;

          const body = stanza.getElementsByTagName('body')[0];
          if (!body) return true;

          const text = body.textContent;
          setMessages((prev) => [
            ...prev,
            {
              id: `bot-pm-${Date.now()}`,
              nick: '',
              text,
              timestamp: Date.now(),
              retracted: false,
              role: 'system',
            },
          ]);

          return true;
        },
        null,
        'message',
        null,
        null,
        null,
      );

      // Handler for presence stanzas (occupant tracking)
      const presHandler = connection.addHandler(
        (stanza) => {
          if (cancelled) return true;

          const from = stanza.getAttribute('from') || '';
          if (from !== roomJid && !from.startsWith(`${roomJid}/`)) return true;

          const nick = from.split('/')[1] || '';
          const type = stanza.getAttribute('type');

          if (type === 'error') {
            const errorEl = stanza.getElementsByTagName('error')[0];
            const code = errorEl?.getAttribute('code') || '';
            const hasConflict =
              code === '409' ||
              errorEl?.getElementsByTagName('conflict').length > 0;

            if (hasConflict) {
              // Nickname conflict: retry with a numeric suffix.
              nickAttemptRef.current += 1;
              if (nickAttemptRef.current < MAX_NICK_ATTEMPTS) {
                const nextNick = `${nickname} ${nickAttemptRef.current + 1}`;
                effectiveNickRef.current = nextNick;
                import('strophe.js').then(({ $pres: pres }) => {
                  if (!cancelled) sendJoinPresence(pres, nextNick, false);
                });
              } else {
                // All attempts exhausted — surface an error to the user.
                setJoinError(true);
              }
            } else {
              // eslint-disable-next-line no-console
              console.warn('[LiveChat] MUC join error, code:', code, stanza);
            }
            return true;
          }

          if (type === 'unavailable') {
            occupantsRef.current.delete(nick);
            const x = stanza.getElementsByTagNameNS(
              'http://jabber.org/protocol/muc#user',
              'x',
            )[0];
            if (x) {
              const statusCodes = x.getElementsByTagName('status');
              const isSelf = Array.from(statusCodes).some(
                (s) => s.getAttribute('code') === '110',
              );
              const isKick = Array.from(statusCodes).some(
                (s) => s.getAttribute('code') === '307',
              );
              if (isSelf && isKick) {
                // We were kicked — show "no longer present" state
                setKicked(true);
                joinedRef.current = false;
              }
            }
          } else {
            // Track role per occupant
            const x = stanza.getElementsByTagNameNS(
              'http://jabber.org/protocol/muc#user',
              'x',
            )[0];
            let role = 'participant';
            if (x) {
              const item = x.getElementsByTagName('item')[0];
              if (item) {
                role = item.getAttribute('role') || 'participant';
              }
            }
            occupantsRef.current.set(nick, role);

            // Check own role from self-presence (status 110 = own presence)
            if (x) {
              const statusCodes = x.getElementsByTagName('status');
              const isSelf = Array.from(statusCodes).some(
                (s) => s.getAttribute('code') === '110',
              );
              if (isSelf) {
                joinedRef.current = true;
                // Lock in the nick that actually joined.
                effectiveNickRef.current = nick;
                setEffectiveNickname(nick);
                const prevRole = ownRoleRef.current;
                // Only update if role actually changed
                if (role && role !== prevRole) {
                  ownRoleRef.current = role;
                  setOwnRole(role);
                }
              }
            }
          }

          setOccupantCount(occupantsRef.current.size);
          setOccupantRoles(Object.fromEntries(occupantsRef.current));
          return true;
        },
        null,
        'presence',
        null,
        null,
        null,
      );

      handlersRef.current = [msgHandler, pmHandler, presHandler];

      // Send initial join presence using the base nickname.
      await sendJoinPresence($pres, nickname);
      // joinedRef is set to true only when self-presence (code 110) is received
    }

    joinRoom();

    return () => {
      cancelled = true;
      if (connection && joinedRef.current) {
        import('strophe.js').then(({ $pres }) => {
          try {
            const leavePres = $pres({
              to: `${roomJid}/${effectiveNickRef.current}`,
              type: 'unavailable',
            });
            connection.send(leavePres);
          } catch {
            // connection may already be closed
          }
        });
        joinedRef.current = false;
      }
      // Read handlers at cleanup time (not capture time) to avoid leak
      handlersRef.current.forEach((h) => {
        try {
          connection.deleteHandler(h);
        } catch {
          // ignore
        }
      });
      handlersRef.current = [];
      // eslint-disable-next-line react-hooks/exhaustive-deps
      occupantsRef.current.clear();
      setMessages([]);
      setOccupantCount(0);
      setOccupantRoles({});
      setEffectiveNickname(null);
      setJoinError(false);
    };
  }, [connection, roomJid, nickname, status]);

  const sendMessage = useCallback(
    async (text) => {
      if (!connection || !roomJid || !text.trim()) return;
      const { $msg } = await import('strophe.js');
      const msg = $msg({ to: roomJid, type: 'groupchat' }).c('body').t(text);
      connection.send(msg);
    },
    [connection, roomJid],
  );

  return {
    messages,
    occupantCount,
    occupantRoles,
    ownRole,
    kicked,
    joinError,
    effectiveNickname,
    sendMessage,
  };
}
